import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { clearAuth, useAuth } from '@/features/auth/authStore';
import { switchOptions as reportSwitchOptions, type DashboardConfig } from '@/features/dashboard/config';
import { useMorrisonsReport } from '@/features/morrisons/useMorrisonsReport';
import { fmtClock, fmtPeriod } from '@/features/morrisons/format';
import { downloadCsv, weeklySummaryCsv } from '@/features/morrisons/csv';
import { Header } from '@/features/morrisons/components/Header';
import {
  CardsCard,
  FlowpacksCard,
  GamesCard,
  MilestonesCard,
  MissionsCard,
  PrizesCard,
  ReachTiles,
  ScanMethodCard,
  StickersCard,
  TakeAway,
  WeeklyCard,
  WeeklyTable,
} from '@/features/morrisons/components/Sections';
import '@/features/morrisons/morrisons.css';

/*
 * Morrisons — Disney × Stickers 2026 (program mr03): thin orchestrator. Fetch
 * (the design's figures in dev until the report answers), then the sections in
 * the design's order: stickers, reach, collection & scanning, flowpacks &
 * prizes, games, missions, weekly summary. Whole campaign throughout; the
 * design has no period picker (one period, 2 Nov → 24 Dec).
 */
export function MorrisonsDashboardPage({ config }: { config: DashboardConfig }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isPreview, lastRefreshedAt } = useMorrisonsReport(config.reportId, config.apiBaseUrl);

  function onSignOut() {
    clearAuth();
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <div className="mo" lang="en-GB">
      <Header
        campaign={data.campaign}
        isPreview={isPreview}
        updatedAt={fmtClock(lastRefreshedAt, data.campaign.timezone)}
        onExport={() => downloadCsv('morrisons-disney-2026-weekly.csv', weeklySummaryCsv(data))}
        switchOptions={reportSwitchOptions(auth?.allowedReports ?? [], auth?.role === 'admin', config.reportId)}
        onSwitch={(p) => navigate(p)}
        isAdmin={auth?.role === 'admin'}
        onManageUsers={() => navigate('/admin/users')}
        onSignOut={onSignOut}
      />
      <main className="mo-wrap mo-main">
        <TakeAway data={data} />
        <StickersCard data={data} />

        <h2 className="mo-section">Reach &amp; activation</h2>
        <ReachTiles data={data} />

        <h2 className="mo-section">Collection &amp; scanning</h2>
        <ScanMethodCard data={data} />
        <div className="mo-grid">
          <MilestonesCard data={data} />
          <CardsCard data={data} />
        </div>

        <h2 className="mo-section">Flowpacks &amp; prizes</h2>
        <div className="mo-grid">
          <FlowpacksCard data={data} />
          <PrizesCard data={data} />
        </div>

        <h2 className="mo-section">Games</h2>
        <GamesCard data={data} />

        <h2 className="mo-section">Missions</h2>
        <MissionsCard data={data} />

        <h2 className="mo-section">Weekly summary</h2>
        <WeeklyCard data={data} />
        <WeeklyTable data={data} />

        <p className="mo-foot">
          KPIs follow §15 “Success metrics / KPIs” of the 2026 Disney Development Team Spec. Campaign runs {fmtPeriod(data.campaign.start, data.campaign.end)} (
          {data.campaign.timezone}); weeks run Monday to Sunday.
        </p>
      </main>
    </div>
  );
}
